import React, {ReactElement} from "react";
import {v4 as uuidv4} from "uuid";

import {FormGroupBase} from "/src/types";
import {CheckboxProps} from "../checkbox/Checkbox";

import {css} from "/src/utils/utils";

import Label from "../label/Label";
import ErrorMessage from "../error-message/ErrorMessage";
import Checkbox from "../checkbox/Checkbox";

import * as styles from "./multi-select.module.scss";

interface MultiSelectProps extends FormGroupBase {
  onChange: (selected: string) => unknown;
  options: Array<CheckboxProps>;
  transparentBg?: boolean;
}

const MultiSelect = (props: MultiSelectProps): ReactElement => {
  const {
    label = "",
    required = false,
    name = "",
    value = "",
    errorMessage = "",
    status,
    disabled = false,
    readOnly = false,
    onChange,
    options,
    darkMode = false,
    transparentBg = false,
  } = props;

  const isInvalid = status === "error";

  const [selectedValue, setSelectedValue] = React.useState<Array<string>>([]);

  React.useEffect(() => {
    setSelectedValue(value != null ? value.split(",") : []);
  }, [value]);

  const handleCheckboxChange = (checkboxValue: string, checked: boolean) => {
    const temp = selectedValue ? [...selectedValue] : [];
    if (checked && !temp.includes(checkboxValue)) {
      temp.push(checkboxValue);
    }

    if (!checked && temp.includes(checkboxValue)) {
      temp.splice(temp.indexOf(checkboxValue), 1);
    }

    setSelectedValue(temp);

    if (onChange) {
      // GROW-2223
      // we are going to sort the selected items,
      // this would make it easier to set the expected value of the conditions
      onChange(
        temp
          .filter(item => item)
          .sort((a, b) => {
            if (a < b) {
              return -1;
            }
            if (a > b) {
              return 1;
            }

            return 0;
          })
          .join(",")
      );
    }
  };

  return (
    <div
      className={css(
        styles.multiSelect,
        darkMode ? styles.multiSelectDark : "",
        transparentBg ? styles.multiSelectTransparent : ""
      )}
    >
      <fieldset className={css(styles.multiSelect__fieldset)}>
        <legend>
          <Label
            darkMode={darkMode}
            disabled={disabled}
            label={label}
            required={required}
          />
        </legend>

        <div className={css(styles.multiSelect__list)}>
          {options &&
            options.map((option: CheckboxProps) => {
              const checkboxProps: CheckboxProps = {
                ...option,
                name,
                checked: selectedValue.includes(option.value!),
                readOnly,
                disabled,
                onChange: handleCheckboxChange,
                status,
                darkMode,
                isPill: true,
                classes: styles.multiSelect__checkbox,
              };
              return (
                <Checkbox
                  key={`${name}-checkbox-item-${
                    option.value != null ? option.value : uuidv4()
                  }`}
                  {...checkboxProps}
                />
              );
            })}
        </div>
      </fieldset>
      <ErrorMessage message={errorMessage} visible={isInvalid} />
    </div>
  );
};

export default MultiSelect;
